// Pure Connection Details module.
//
// Owns the shape and meaning of the rich, entry-level detail a Rolodex Entry
// carries about a Person: photo, birthday, socials, and the four additive
// lists (moments, career, education, locations). No React, no Firebase.
//
// firestore.rules validates these keys only coarsely (`is list`, `is map`,
// size caps) because rules cannot practically walk list elements. Element
// shape is enforced here, on the way in, exactly as `private_profile` is.

export const MAX_LIST_ITEMS = 50;
export const MAX_TEXT_LENGTH = 300;

export const SOCIAL_KEYS = Object.freeze(["linkedin", "instagram", "x", "website"]);

export const DETAIL_LIST_KEYS = Object.freeze([
  "moments",
  "career",
  "education",
  "locations",
]);

export const CONNECTION_DETAIL_KEYS = Object.freeze([
  "photo_url",
  "birthday",
  "socials",
  ...DETAIL_LIST_KEYS,
]);

// Text fields per list, plus which lists carry a `current` flag.
const LIST_TEXT_FIELDS = Object.freeze({
  moments: ["title", "date", "note"],
  career: ["title", "org", "start", "end"],
  education: ["school", "degree", "year"],
  locations: ["label"],
});

const LIST_FLAG_FIELDS = Object.freeze({
  career: ["current"],
  locations: ["current"],
});

// The field that must be present for an element to be worth storing.
const LIST_REQUIRED_FIELD = Object.freeze({
  moments: "title",
  career: "title",
  education: "school",
  locations: "label",
});

function text(value, max = MAX_TEXT_LENGTH) {
  return typeof value === "string" ? value.trim().slice(0, max) : "";
}

/** djb2 — deterministic ids for elements that arrive without one, so that
 *  normalizing the same input twice produces the same document. */
function contentId(listKey, element) {
  const source = `${listKey}:${LIST_TEXT_FIELDS[listKey]
    .map((field) => text(element?.[field]))
    .join("|")}`;
  let hash = 5381;
  for (let index = 0; index < source.length; index += 1) {
    hash = ((hash << 5) + hash + source.charCodeAt(index)) >>> 0;
  }
  return `${listKey.slice(0, 3)}_${hash.toString(36)}`;
}

/** Id for a brand-new element created in the UI. Not used by normalization. */
export function newDetailId(listKey = "item") {
  return `${listKey.slice(0, 3)}_${Date.now().toString(36)}${Math.random()
    .toString(36)
    .slice(2, 7)}`;
}

// "2024-06" and "2024" sort correctly against "2024-06-14" as plain strings,
// so date ordering needs no parsing. Blank sorts last.
function descending(getKey) {
  return (a, b) => {
    const left = getKey(a) || "";
    const right = getKey(b) || "";
    if (left === right) return 0;
    if (!left) return 1;
    if (!right) return -1;
    return left < right ? 1 : -1;
  };
}

const LIST_SORTERS = Object.freeze({
  moments: descending((item) => item.date),
  career: (a, b) => {
    if (Boolean(a.current) !== Boolean(b.current)) return a.current ? -1 : 1;
    return descending((item) => item.start)(a, b);
  },
  education: descending((item) => item.year),
  locations: (a, b) => {
    if (Boolean(a.current) !== Boolean(b.current)) return a.current ? -1 : 1;
    return 0;
  },
});

function normalizeList(listKey, value) {
  if (!Array.isArray(value)) return [];
  const seenIds = new Set();
  const elements = value
    .map((element) => {
      if (!element || typeof element !== "object") return null;
      const normalized = {};
      LIST_TEXT_FIELDS[listKey].forEach((field) => {
        const cleaned = text(element[field]);
        if (cleaned) normalized[field] = cleaned;
      });
      if (!normalized[LIST_REQUIRED_FIELD[listKey]]) return null;
      (LIST_FLAG_FIELDS[listKey] || []).forEach((field) => {
        if (element[field] === true) normalized[field] = true;
      });
      const id = text(element.id, 64) || contentId(listKey, element);
      if (seenIds.has(id)) return null;
      seenIds.add(id);
      return { id, ...normalized };
    })
    .filter(Boolean);

  return elements.sort(LIST_SORTERS[listKey]).slice(0, MAX_LIST_ITEMS);
}

function normalizeSocials(value) {
  if (!value || typeof value !== "object") return {};
  return Object.fromEntries(
    SOCIAL_KEYS.map((key) => [key, text(value[key])]).filter(([, url]) => url)
  );
}

/** "YYYY-MM-DD" only; anything else is dropped rather than half-stored. */
function normalizeBirthday(value) {
  const cleaned = text(value, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(cleaned) ? cleaned : "";
}

/**
 * Clean the detail fields of one entry. Empty values are omitted entirely so
 * Firestore documents stay free of blank keys, matching how
 * `normalizePrivateContactProfile` treats the contact card.
 */
export function normalizeConnectionDetails(entry) {
  const details = {};

  const photo = text(entry?.photo_url, 2000);
  if (photo) details.photo_url = photo;

  const birthday = normalizeBirthday(entry?.birthday);
  if (birthday) details.birthday = birthday;

  const socials = normalizeSocials(entry?.socials);
  if (Object.keys(socials).length) details.socials = socials;

  DETAIL_LIST_KEYS.forEach((listKey) => {
    const list = normalizeList(listKey, entry?.[listKey]);
    if (list.length) details[listKey] = list;
  });

  return details;
}

// ── Closeness ──────────────────────────────────────────────────────────────
// The UI asks for 1–5. `connection_strength` (weak/moderate/strong) stays the
// stored truth for the graph, sidebar, and search, and is derived from it, so
// no existing entry needs migrating.

export const CLOSENESS_MIN = 1;
export const CLOSENESS_MAX = 5;

export function strengthFromCloseness(closeness) {
  const value = Number(closeness);
  if (!Number.isFinite(value)) return "moderate";
  if (value <= 2) return "weak";
  if (value >= 4) return "strong";
  return "moderate";
}

export function closenessFromStrength(strength) {
  if (strength === "weak") return 2;
  if (strength === "strong") return 4;
  return 3;
}

/** Explicit closeness wins; otherwise derive it from the stored strength. */
export function closenessFor(facet) {
  const value = Number(facet?.closeness);
  if (
    Number.isInteger(value) &&
    value >= CLOSENESS_MIN &&
    value <= CLOSENESS_MAX
  ) {
    return value;
  }
  return closenessFromStrength(facet?.connection_strength);
}

// ── Display helpers ────────────────────────────────────────────────────────

/**
 * Grow the legacy scalar contact fields into the first element of their list,
 * for entries saved before those lists existed.
 *
 * Seeds only when the list key is *absent*. An entry whose list is present but
 * empty has had every row deleted on purpose, and must stay empty.
 */
export function seedDetailsFromPrivateProfile(entry, contactProfile = {}) {
  const profile = { ...(entry?.private_profile || {}), ...contactProfile };
  const seeded = {};

  if (entry?.career === undefined) {
    const title = text(profile.role);
    const org = text(profile.company) || text(profile.organization);
    if (title || org) {
      seeded.career = [
        { id: "car_seed", ...(title ? { title } : {}), ...(org ? { org } : {}), current: true },
      ];
    }
  }

  if (entry?.education === undefined) {
    const school = text(profile.school);
    if (school) seeded.education = [{ id: "edu_seed", school }];
  }

  if (entry?.locations === undefined) {
    const label = text(profile.location);
    if (label) seeded.locations = [{ id: "loc_seed", label, current: true }];
  }

  return seeded;
}
